import React, { useCallback, useMemo, useState } from "react";
import { Field } from "react-final-form";
import { Box } from "@mui/material";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import dayjs from "dayjs";
import RffInputWrapper from "../shared/RffInputWrapper";
import DateOfBirthInput from "../../inputs/DateOfBirthInput";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import colors from "../../../theme/colors";
import {
  calculateAge,
  isBirthdayToday,
  parseDateOfBirth,
} from "../../../utils/dates/dateOfBirth";

const DEFAULT_MIN_AGE = 18;
const MIN_YEAR = 1900;

// Reasons MUI's own picker validation reports. These never reach the form value
// (an unparseable entry submits as empty), so they are surfaced in the status
// line instead of through final-form's meta.error.
const PICKER_ERROR_MESSAGES = {
  invalidDate: "Enter a valid date in MM/DD/YYYY format",
  disableFuture: "Date of birth can't be in the future",
  maxDate: "Date of birth can't be in the future",
  minDate: `Enter a date after January 1, ${MIN_YEAR}`,
};

/**
 * The line under the field: the computed age is what an underwriter actually
 * reads off a DOB, so show it as soon as the date is valid rather than making
 * them do the arithmetic. Height is reserved so the form doesn't jump when the
 * message appears.
 */
const DobStatusLine = ({ errorText, age, minAge, isBirthday }) => {
  if (errorText) {
    return (
      <Flx centerY gap={0.75} sx={{ minHeight: 20, color: colors.dangerFg }}>
        <ErrorOutlineIcon sx={{ fontSize: 15 }} />
        <Txt variant="caption" color="inherit">
          {errorText}
        </Txt>
      </Flx>
    );
  }

  if (age === null || age === undefined) return null;
  // if (age === null || age === undefined) return <Box sx={{ minHeight: 20 }} />;

  if (age < minAge) {
    return (
      <Flx centerY gap={0.75} sx={{ minHeight: 20, color: colors.warningFg }}>
        <WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />
        <Txt variant="caption" color="inherit">
          {`Applicant is ${age} — borrowers must be ${minAge} or older`}
        </Txt>
      </Flx>
    );
  }

  return (
    <Flx
      centerY
      gap={0.5}
      sx={{ minHeight: 20, fontSize: 11, color: colors.successFg }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />
      <Txt variant="caption" color="inherit">
        You are
      </Txt>
      <Box
        sx={{
          backgroundColor: colors.infoBg,
          color: colors.infoFg,
          fontSize: 11,
          fontWeight: 600,
          borderRadius: 999,
          px: 1,
          // ml: 0.25,
          py: 0.125,
        }}
      >
        {`${age} years old`}
      </Box>
      {isBirthday && (
        <Flx centerY gap={0.5} sx={{ color: colors.infoFg, ml: 0.5 }}>
          <CakeOutlinedIcon
            sx={{ fontSize: "15px !important", color: colors.infoFg }}
          />
          <Txt
            variant="caption"
            color="inherit"
            sx={{
              fontSize: 11,
              lineHeight: 1,
              fontWeight: 600,
              color: colors.infoFg,
            }}
          >
            Happy birthday!
          </Txt>
        </Flx>
      )}
    </Flx>
  );
};

/**
 * RffDateOfBirthField — RffDateField specialized for a birth date.
 *
 * Differences from RffDateField:
 *   - the calendar opens year-first (year → month → day) near a plausible birth
 *     year, so reaching 1988 is two clicks rather than 450 month steps
 *   - future dates and years before 1900 are unreachable and rejected
 *   - the computed age is shown live under the field, with an eligibility
 *     warning below `minAge`
 *   - the value is a date-only "YYYY-MM-DD" string, not a full ISO timestamp
 *     (RffDateField's format), so the calendar date can't shift by a day
 *
 * `enforceMinAge` promotes the under-age warning to a blocking validation error;
 * it stays a warning by default so a broker can finish the application and let
 * the lender decide.
 */
const RffDateOfBirthField = ({
  name,
  label,
  required = false,
  helperText,
  fullWidth = true,
  row = false,
  suppressGrid,
  size,
  disabled,
  onBlur,
  minAge = DEFAULT_MIN_AGE,
  enforceMinAge = false,
}) => {
  const [pickerError, setPickerError] = useState(null);

  const minDate = useMemo(() => dayjs(`${MIN_YEAR}-01-01`), []);
  const maxDate = useMemo(() => dayjs().startOf("day"), []);

  const validate = useCallback(
    (value) => {
      if (value == null || value === "") {
        return required ? "This field is required" : undefined;
      }
      const parsed = parseDateOfBirth(value);
      if (!parsed) return "Enter a valid date in MM/DD/YYYY format";
      if (parsed.isAfter(maxDate))
        return "Date of birth can't be in the future";
      if (parsed.year() < MIN_YEAR) {
        return `Enter a date after January 1, ${MIN_YEAR}`;
      }
      if (enforceMinAge && calculateAge(value) < minAge) {
        return `Borrowers must be at least ${minAge} years old`;
      }
      return undefined;
    },
    [required, maxDate, enforceMinAge, minAge],
  );

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      helperText={helperText}
      required={required}
      row={row}
    >
      <Field
        name={name}
        validate={validate}
        parse={(v) => (v === "" ? undefined : v)}
        render={({ input, meta }) => {
          const formError = meta.touched && meta.error ? meta.error : null;
          // The picker's own reasons fire while typing, before the field is
          // touched — showing them immediately is the point of a masked input.
          const errorText = PICKER_ERROR_MESSAGES[pickerError] || formError;
          const age = errorText ? null : calculateAge(input.value);

          return (
            <>
              <DateOfBirthInput
                {...input}
                onBlur={(event) => {
                  input.onBlur(event);
                  if (onBlur) onBlur(event);
                }}
                onError={setPickerError}
                disabled={disabled}
                fullWidth={fullWidth}
                minDate={minDate}
                maxDate={maxDate}
                error={!!errorText}
              />
              <DobStatusLine
                errorText={errorText}
                age={age}
                minAge={minAge}
                isBirthday={!errorText && isBirthdayToday(input.value)}
              />
            </>
          );
        }}
      />
    </RffInputWrapper>
  );
};

export default RffDateOfBirthField;
